import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { AuthContext } from '../context/AuthContext';
import { cuplAPI } from '../context/api';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import axios from '../api/axiosConfig';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Estados para filtros
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeFilter, setActiveFilter] = useState('day');

  // Estados para datos
  const [stats, setStats] = useState(null);
  const [tramiteStats, setTramiteStats] = useState([]);
  const [balance, setBalance] = useState(0);
  const [recentCUPLs, setRecentCUPLs] = useState([]);
  const [chartData, setChartData] = useState({ paidCupls: [], income: [] });
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const [statsRes, tramiteRes, balanceRes, recentRes, chartRes] = await Promise.all([
        cuplAPI.getStatistics(params.toString() ? `?${params.toString()}` : ''),
        axios.get(`/api/transitos/stats/plates?${params.toString()}`),
        axios.get(`/api/cash/balance?${params.toString()}`),
        axios.get(`/api/cupl?${params.toString()}&limit=5`),
        cuplAPI.getChartStats(params.toString() ? `?${params.toString()}` : '')
      ]);

      setStats(statsRes.data);
      setTramiteStats(tramiteRes.data);
      setBalance(balanceRes.data.balance || 0);
      setRecentCUPLs(Array.isArray(recentRes.data) ? recentRes.data.slice(0, 5) : []);
      setChartData(chartRes.data);
    } catch (err) {
      console.error('Error al cargar datos del dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const setFilterRange = (type) => {
    const today = new Date();
    let start = new Date();

    if (type === 'day') {
      start = today;
    } else if (type === 'week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Lunes
      start = new Date(today.setDate(diff));
    } else if (type === 'month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setActiveFilter(type);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`Fecha: ${label}`}</p>
          <p className="value">
            {payload[0].name === 'total'
              ? `Ingreso: ${formatCurrency(payload[0].value)}`
              : `Pagados: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const totalPaidInChart = Array.isArray(chartData?.paidCupls) 
    ? chartData.paidCupls.reduce((acc, curr) => acc + parseInt(curr.count || 0), 0)
    : 0;

  const totalIncomeInChart = Array.isArray(chartData?.income) 
    ? chartData.income.reduce((acc, curr) => acc + parseFloat(curr.total || 0), 0)
    : 0;

  // Determinar si el usuario es administrador de la agencia (ve saldo y graficos de ingresos)
  const isAgencyAdmin = user?.role === 'superadmin' || (!user?.concesionario_id && !user?.transito_id);

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="📊 Dashboard" />

        <div className="page-content">
          {/* Filtros de Fecha Rápidos */}
          <div className="dashboard-controls">
            <div className="quick-filters">
              <button
                className={`btn-filter ${activeFilter === 'day' ? 'active' : ''}`}
                onClick={() => setFilterRange('day')}
              >
                Hoy
              </button>
              <button
                className={`btn-filter ${activeFilter === 'week' ? 'active' : ''}`}
                onClick={() => setFilterRange('week')}
              >
                Esta Semana
              </button>
              <button
                className={`btn-filter ${activeFilter === 'month' ? 'active' : ''}`}
                onClick={() => setFilterRange('month')}
              >
                Este Mes
              </button>
            </div>

            <div className="dashboard-filters">
              <div className="filter-group">
                <label>Desde:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setActiveFilter('custom');
                  }}
                />
              </div>
              <div className="filter-group">
                <label>Hasta:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setActiveFilter('custom');
                  }}
                />
              </div>
              <button className="btn-refresh" onClick={fetchDashboardData}>
                🔄 Actualizar
              </button>
            </div>
          </div>

          {/* Tarjetas de Resumen Principal */}
          <div className="stats-grid">
            {isAgencyAdmin ? (
              <div className="stat-card primary">
                <div className="stat-icon">💰</div>
                <div className="stat-details">
                  <span className="stat-label">Saldo en Caja</span>
                  <span className="stat-value">{formatCurrency(balance)}</span>
                </div>
              </div>
            ) : (
              <div className="stat-card primary">
                <div className="stat-icon">🆔</div>
                <div className="stat-details">
                  <span className="stat-label">Placas Disponibles</span>
                  <span className="stat-value">
                    {tramiteStats.reduce((acc, curr) => acc + (curr.disponibles || 0), 0)}
                  </span>
                </div>
              </div>
            )}

            <div className="stat-card success">
              <div className="stat-icon">✅</div>
              <div className="stat-details">
                <span className="stat-label">CUPLs Pagados</span>
                <span className="stat-value">{stats?.total_pagados || 0}</span>
              </div>
            </div>

            {isAgencyAdmin && (
              <div className="stat-card danger">
                <div className="stat-icon">💸</div>
                <div className="stat-details">
                  <span className="stat-label">Gastos CUPLs Pagados</span>
                  <span className="stat-value">{formatCurrency(stats?.total_gastos_pagados || 0)}</span>
                </div>
              </div>
            )}

            <div className="stat-card warning">
              <div className="stat-icon">⏳</div>
              <div className="stat-details">
                <span className="stat-label">CUPLs Pendientes</span>
                <span className="stat-value">{stats?.total_pendientes || 0}</span>
              </div>
            </div>

            <div className="stat-card danger">
              <div className="stat-icon">🚫</div>
              <div className="stat-details">
                <span className="stat-label">CUPLs Vencidos</span>
                <span className="stat-value">{stats?.total_vencidos || 0}</span>
              </div>
            </div>

            <div className="stat-card info">
              <div className="stat-icon">📑</div>
              <div className="stat-details">
                <span className="stat-label">Total Trámites</span>
                <span className="stat-value">
                  {tramiteStats.reduce((acc, curr) => acc + (curr.count !== undefined ? curr.count : (curr.total || 0)), 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Sección de Gráficos */}
          <div className="dashboard-charts-grid" style={{ gridTemplateColumns: isAgencyAdmin ? '1fr 1fr' : '1fr' }}>
            <div className="dashboard-section chart-section">
              <div className="section-header">
                <h3>📈 CUPLs Pagados (Total: {totalPaidInChart})</h3>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.paidCupls}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--sidebar-border)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--secondary-color)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="var(--secondary-color)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="count"
                      fill="var(--primary-color)"
                      radius={[4, 4, 0, 0]}
                      barSize={30}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {isAgencyAdmin && (
              <div className="dashboard-section chart-section">
                <div className="section-header">
                  <h3>💵 Ingresos (Total: {formatCurrency(totalIncomeInChart)})</h3>
                </div>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData.income}>
                      <defs>
                        <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--sidebar-border)" />
                      <XAxis
                        dataKey="date"
                        stroke="var(--secondary-color)"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="var(--secondary-color)"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `$${value / 1000}k`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke="#10b981"
                        fillOpacity={1}
                        fill="url(#colorIncome)"
                        strokeWidth={3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          <div className="dashboard-main-grid">
            {/* Gráfico/Resumen de Trámites/Placas por Organismo */}
            <div className="dashboard-section main-section">
              <div className="section-header">
                <h3>🏢 Inventario de Placas por Organismo</h3>
                <button onClick={() => navigate('/plates')} className="btn-text">Ver todo →</button>
              </div>
              <div className="organism-list">
                {tramiteStats.length > 0 ? (
                  tramiteStats.slice(0, 6).map((ts, idx) => {
                    const count = ts.count !== undefined ? ts.count : (ts.total || 0);
                    return (
                      <div key={idx} className="organism-item">
                        <div className="organism-info">
                          <span className="organism-name">{ts.transito_name}</span>
                          <span className="organism-type">
                            {ts.vehicle_type === 'moto' ? '🏍️ Moto' : 
                             ts.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : 
                             '🚗 Carro'}
                          </span>
                        </div>
                        <div className="organism-bar-container">
                          <div
                            className="organism-bar"
                            style={{ width: `${Math.min((count / 50) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <span className="organism-count">{count}</span>
                      </div>
                    );
                  })
                ) : (
                  <p className="no-data">No hay datos para el rango seleccionado</p>
                )}
              </div>
            </div>

            {/* CUPLs Recientes */}
            <div className="dashboard-section">
              <div className="section-header">
                <h3>🕒 Actividad Reciente</h3>
                <button onClick={() => navigate('/cupl-payments')} className="btn-text">Ver todos →</button>
              </div>
              <div className="recent-list">
                {recentCUPLs.length > 0 ? (
                  recentCUPLs.map((cupl) => (
                    <div key={cupl.id} className="recent-item">
                      <div className="recent-icon">📄</div>
                      <div className="recent-info">
                        <span className="recent-title">{cupl.plate} - {cupl.cupl_number}</span>
                        <span className="recent-subtitle">{cupl.tramite_name}</span>
                      </div>
                      <div className={`recent-status ${cupl.is_paid ? 'paid' : 'pending'}`}>
                        {cupl.is_paid ? 'Pagado' : 'Pendiente'}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">No hay actividad reciente</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
